import React from 'react';
import { updatePnlDropdowns, PnlLevel, fetchPnlYears } from '../pnl/api';
import Dropdowns from '../../components/Dropdowns';
import { StatsCard } from "components/ui/stats-card"
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useAuth } from 'contexts/AuthContext';
import { fetchOuAnalysisStats, OuAnalysisResponse, fetchOuDrilldown, OuTableRow, OuBucketData, MetricType } from './api';
import AnalysisTable from './components/AnalysisTable';
import { cn } from 'lib/utils';
import { StatsSkeleton } from 'components/ui/stats-skeleton';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';

function useStableRef<T>(initial: T) {
  const ref = React.useRef(initial);
  return ref;
}

// Month data ordered Apr to Mar (financial year order)
const monthsData = [
  { value: '4', label: 'Apr', fullLabel: 'April' },
  { value: '5', label: 'May', fullLabel: 'May' },
  { value: '6', label: 'Jun', fullLabel: 'June' },
  { value: '7', label: 'Jul', fullLabel: 'July' },
  { value: '8', label: 'Aug', fullLabel: 'August' },
  { value: '9', label: 'Sep', fullLabel: 'September' },
  { value: '10', label: 'Oct', fullLabel: 'October' },
  { value: '11', label: 'Nov', fullLabel: 'November' },
  { value: '12', label: 'Dec', fullLabel: 'December' },
  { value: '1', label: 'Jan', fullLabel: 'January' },
  { value: '2', label: 'Feb', fullLabel: 'February' },
  { value: '3', label: 'Mar', fullLabel: 'March' },
];

// Metric labels mapping
const metricLabels: Record<MetricType, string> = {
  cm: 'CM',
  cm1: 'CM1',
  cm2: 'CM2',
  ebitda: 'EBITDA',
  ebit: 'EBIT',
  pbt: 'PBT',
};

// Narrow type used for drilldown requests (works for both buckets and total)
type DrillBucket = Pick<OuBucketData, 'title' | 'min_pct' | 'max_pct' | 'min_inclusive' | 'max_inclusive'>;

const OuAnalysisPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { permissions } = useAuth();
  const [years, setYears] = React.useState<string[]>([]);
  const [year, setYear] = React.useState<string>('');
  const [levels, setLevels] = React.useState<PnlLevel[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [selectedMonths, setSelectedMonths] = React.useState<string[]>([]);
  const [analysisData, setAnalysisData] = React.useState<OuAnalysisResponse | null>(null);
  const [analysisLoading, setAnalysisLoading] = React.useState<boolean>(false);
  const [analysisError, setAnalysisError] = React.useState<string>('');
  const [metric, setMetric] = React.useState<MetricType | null>(null);
  const [allowedMetrics, setAllowedMetrics] = React.useState<MetricType[]>([]);

  // Smooth loading states
  const { isLoading: smoothAnalysisLoading, showContent: showAnalysisContent } = useSmoothLoading(analysisLoading, {
    minLoadingTime: 200,
    debounceTime: 200
  });

  // OU table state
  const [showOuTable, setShowOuTable] = React.useState<boolean>(false);
  const [ouData, setOuData] = React.useState<OuTableRow[]>([]);
  const [ouTotal, setOuTotal] = React.useState<number>(0);
  const [ouLoading, setOuLoading] = React.useState<boolean>(false);
  const [ouError, setOuError] = React.useState<string>('');
  const [selectedBucket, setSelectedBucket] = React.useState<DrillBucket | null>(null);
  const [ouConversionType, setOuConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('INR');
  const [ouSort, setOuSort] = React.useState<{ by: string; dir: 'asc' | 'desc' }>({ by: 'ou_name', dir: 'asc' });
  const [ouPage, setOuPage] = React.useState<number>(1);
  const [ouPageSize] = React.useState<number>(20);
  const [ouHasLoadedOnce, setOuHasLoadedOnce] = React.useState<boolean>(false);

  const didInitRef = useStableRef(false);

  const fetchLevels = React.useCallback(async (selectionMap?: Record<string, string[]>) => {
    if (!year) return;
    setLoading(true);
    setError('');
    try {
      const data = await updatePnlDropdowns({ year, selection: selectionMap || {} });
      setLevels(Array.isArray(data?.levels) ? data.levels : []);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Something went wrong';
      setError(message);
      if (isForbidden) setLevels([]);
    } finally {
      setLoading(false);
    }
  }, [year]);

  const buildSelectionAndFiltersFromLevels = React.useCallback((currentLevels: PnlLevel[]) => {
    const selection: Record<string, string[]> = {};
    const filters: Record<string, string[]> = {};

    for (const level of currentLevels) {
      if (level && level.selected && level.selected.length > 0) {
        selection[String(level.id)] = level.selected;
      }
    }

    return { selection, filters: Object.keys(selection).length > 0 ? {} : filters };
  }, []);

  const fetchAnalysisData = React.useCallback(async (currentLevels: PnlLevel[], months: string[], currentMetric?: MetricType | null) => {
    if (!year) return;
    const { selection, filters } = buildSelectionAndFiltersFromLevels(currentLevels);
    const monthsToSend = months.length > 0 ? months.map(m => parseInt(m)) : undefined;

    setAnalysisLoading(true);
    setAnalysisError('');
    try {
      const data = await fetchOuAnalysisStats({
        year,
        ...(Object.keys(selection).length > 0 ? { selection } : { filters }),
        ...(monthsToSend && { months: monthsToSend }),
        ...(currentMetric && { metric: currentMetric })
      });
      setAnalysisData(data);

      // Always sync metric state with backend's meta.metric (source of truth)
      if (data.meta) {
        setMetric(data.meta.metric);
        setAllowedMetrics(data.meta.allowed_metrics || []);
      }
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Failed to fetch analysis data';
      setAnalysisError(message);
      if (isForbidden) setAnalysisData(null);
    } finally {
      setAnalysisLoading(false);
    }
  }, [buildSelectionAndFiltersFromLevels, year]);

  React.useEffect(() => {
    setBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard/ou-analysis' },
      { label: 'Branch/Project Analysis' },
    ]);
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      try {
        const resp = await fetchPnlYears();
        const yrList = Array.isArray(resp?.years) ? resp.years : [];
        setYears(yrList);
        setYear(yrList[0] || '');
      } catch (e) {
        setYears([]);
        setYear('');
      }
    })();
  }, [didInitRef, setBreadcrumbs]);

  React.useEffect(() => {
    if (!year) return;
    setLevels([]);
    setAnalysisData(null);
    setMetric(null);
    setAllowedMetrics([]);
    fetchLevels({});
    fetchAnalysisData([], selectedMonths, null);

    // Clear table state and load total table by default
    setShowOuTable(false);
    setOuData([]);
    setOuError('');
    setOuHasLoadedOnce(false);

    const totalBucket: DrillBucket = {
      title: 'Total',
      min_pct: null,
      max_pct: null,
      min_inclusive: false,
      max_inclusive: false,
    };
    setSelectedBucket(totalBucket);
    setShowOuTable(true);
    setOuPage(1);
  }, [year, fetchLevels, fetchAnalysisData]);

  const buildSelectionFromLevels = React.useCallback((currentLevels: PnlLevel[]) => {
    const map: Record<string, string[]> = {};
    for (const level of currentLevels) {
      if (level && level.selected && level.selected.length > 0) {
        map[String(level.id)] = level.selected;
      }
    }
    return map;
  }, []);

  const handleSelectChange = React.useCallback((levelId: number | string, value: string[]) => {
    const desc = (levels || [])
      .filter((l) => Boolean(l?.visible))
      .sort((a, b) => Number(b.id) - Number(a.id));
    const indexById = Object.fromEntries(desc.map((l, i) => [String(l.id), i]));
    const changedIdx = indexById[String(levelId)];

    const nextLevels = levels.map((lvl) => {
      const idx = indexById[String(lvl.id)];
      if (idx < changedIdx) {
        return lvl.id === levelId ? { ...lvl, selected: value.length > 0 ? value : null } : lvl;
      }
      if (idx === changedIdx) {
        return { ...lvl, selected: value.length > 0 ? value : null };
      }
      return { ...lvl, selected: null };
    });

    setLevels(nextLevels);
    const selection = buildSelectionFromLevels(nextLevels);
    if (value.length === 0) delete selection[String(levelId)];
    fetchLevels(selection);
    fetchAnalysisData(nextLevels, selectedMonths, metric);
  }, [levels, buildSelectionFromLevels, fetchLevels, fetchAnalysisData, selectedMonths, metric]);

  const handleYearChange = React.useCallback((newYear: string) => {
    if (!newYear) return;
    setYear(newYear);
  }, []);

  const handleBatchSelect = React.useCallback((updates: Array<{ levelId: number | string; value: string[] }>) => {
    if (!Array.isArray(updates) || updates.length === 0) return;

    const updatesById = Object.fromEntries(updates.map(u => [String(u.levelId), u.value]));

    const nextLevels = (levels || []).map((lvl) => {
      const updated = updatesById[String(lvl.id)];
      if (updated) {
        return { ...lvl, selected: updated };
      }
      return lvl;
    });

    setLevels(nextLevels);
    const selection = buildSelectionFromLevels(nextLevels);
    fetchLevels(selection);
    fetchAnalysisData(nextLevels, selectedMonths, metric);
  }, [levels, buildSelectionFromLevels, fetchLevels, fetchAnalysisData, selectedMonths, metric]);

  const handleMonthsChange = React.useCallback((months: string[]) => {
    setSelectedMonths(months);
    fetchAnalysisData(levels, months, metric);
  }, [levels, fetchAnalysisData, metric]);

  const handleMetricChange = React.useCallback((newMetric: MetricType) => {
    setMetric(newMetric);
    fetchAnalysisData(levels, selectedMonths, newMetric);
  }, [levels, selectedMonths, fetchAnalysisData]);

  const fetchOuData = React.useCallback(async (bucket: DrillBucket) => {
    if (!year) return;
    const { selection, filters } = buildSelectionAndFiltersFromLevels(levels);
    const monthsToSend = selectedMonths.length > 0 ? selectedMonths.map(m => parseInt(m)) : undefined;

    setOuLoading(true);
    setOuError('');
    try {
      const data = await fetchOuDrilldown({
        year,
        ...(Object.keys(selection).length > 0 ? { selection } : { filters }),
        ...(monthsToSend && { months: monthsToSend }),
        ...(metric && { metric }),
        bucket: {
          min_pct: bucket.min_pct,
          max_pct: bucket.max_pct,
          min_inclusive: bucket.min_inclusive,
          max_inclusive: bucket.max_inclusive,
        },
        sort: {
          by: ouSort.by as any,
          dir: ouSort.dir,
        },
        page: ouPage,
        page_size: ouPageSize,
      });
      setOuData(data.items);
      setOuTotal(data.total);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Failed to fetch OU data';
      setOuError(message);
      if (isForbidden) {
        setOuData([]);
        setOuTotal(0);
      }
    } finally {
      setOuLoading(false);
      setOuHasLoadedOnce(true);
    }
  }, [buildSelectionAndFiltersFromLevels, levels, selectedMonths, year, metric, ouSort.by, ouSort.dir, ouPage, ouPageSize]);

  const handleTotalClick = React.useCallback(() => {
    const totalBucket: DrillBucket = {
      title: 'Total',
      min_pct: null,
      max_pct: null,
      min_inclusive: false,
      max_inclusive: false,
    };

    setSelectedBucket(totalBucket);
    setShowOuTable(true);
    setOuPage(1);
  }, []);

  const handleBucketClick = React.useCallback((bucket: DrillBucket) => {
    setSelectedBucket(bucket);
    setShowOuTable(true);
    setOuPage(1); // Reset to first page
  }, []);

  const handleCloseOuTable = React.useCallback(() => {
    setShowOuTable(false);
    setSelectedBucket(null);
    setOuData([]);
    setOuTotal(0);
    setOuError('');
  }, []);

  const handleOuSort = React.useCallback((by: string, dir: 'asc' | 'desc') => {
    setOuSort({ by, dir });
    setOuPage(1); // Reset to first page
  }, []);

  const handleOuPageChange = React.useCallback((page: number) => {
    setOuPage(page);
  }, []);

  // Effect to handle OU data refetching
  React.useEffect(() => {
    if (!year) return;
    if (!selectedBucket || !showOuTable) return;
    if (levels.length === 0) return;
    fetchOuData(selectedBucket);
  }, [ouSort, ouPage, levels, selectedMonths, fetchOuData, selectedBucket, showOuTable]);

  return (
    <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
      {/* Filters Section */}
      <div className="space-y-4">
        <Dropdowns
          year={year}
          years={years}
          levels={levels}
          loading={loading}
          onSelectChange={handleSelectChange}
          onBatchSelect={handleBatchSelect}
          onYearChange={handleYearChange}
        />

        {/* Month Selector Pills and Metric Dropdown */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {monthsData.map((month) => {
                const isSelected = selectedMonths.includes(month.value);
                return (
                  <button
                    key={month.value}
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      const newSelection = isSelected
                        ? selectedMonths.filter(m => m !== month.value)
                        : [...selectedMonths, month.value];
                      handleMonthsChange(newSelection);
                    }}
                    className={cn(
                      "px-3 py-1.5 text-sm font-medium rounded-full border transition-colors",
                      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                      isSelected
                        ? "bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
                      loading && "opacity-50 cursor-not-allowed"
                    )}
                    title={month.fullLabel}
                  >
                    {month.label}
                  </button>
                );
              })}
            </div>

            {/* Metric Dropdown */}
            {metric && allowedMetrics.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Profit Metric:</span>
                <Select
                  value={metric}
                  onValueChange={(value) => handleMetricChange(value as MetricType)}
                  disabled={loading || analysisLoading}
                >
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue placeholder="Select metric" />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedMetrics.map((m) => (
                      <SelectItem key={m} value={m}>
                        {metricLabels[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* Analysis Cards Section */}
      {smoothAnalysisLoading && (
        <div className="space-y-6">
          <StatsSkeleton count={6} />
        </div>
      )}

      {analysisError && !smoothAnalysisLoading && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">{analysisError}</p>
        </div>
      )}

      {analysisData && showAnalysisContent && (
        <div className={`space-y-6 transition-opacity duration-300 ${showAnalysisContent ? 'opacity-100' : 'opacity-0'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Total */}
            <StatsCard
              title={analysisData.cards.total.title}
              count={analysisData.cards.total.metrics.ous}
              cm={analysisData.cards.total.metrics.profit}
              cmPct={analysisData.cards.total.metrics.profit_pct}
              isTotal
              onClick={handleTotalClick}
            />

            {/* Buckets */}
            {analysisData.cards.buckets
              .sort((a, b) => a.order - b.order)
              .map((b) => (
                <StatsCard
                  key={b.title}
                  title={b.title}
                  count={b.metrics.ous}
                  cm={b.metrics.profit}
                  cmPct={b.metrics.profit_pct}
                  onClick={() => handleBucketClick(b)}
                />
              ))}
          </div>
        </div>
      )}

      {/* OU Table Section */}
      {showOuTable && (
        <div className="space-y-4">
          {ouError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{ouError}</p>
            </div>
          )}

          <AnalysisTable
            data={ouData}
            total={ouTotal}
            loading={ouLoading}
            conversionType={ouConversionType}
            onConversionChange={setOuConversionType}
            sortBy={ouSort.by}
            sortDir={ouSort.dir}
            onSort={handleOuSort}
            page={ouPage}
            pageSize={ouPageSize}
            onPageChange={handleOuPageChange}
            onClose={handleCloseOuTable}
            bucketTitle={selectedBucket?.title}
            isOuView={true}
            hasLoadedOnce={ouHasLoadedOnce}
          />
        </div>
      )}
    </div>
  );
};

export default OuAnalysisPage;
